import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { getWebSocketUrl } from '../config'
import { ClientMessage, ServerMessage, validateServerMessage } from '../../shared/ws-messages'
import { appendClerkTokenToWebSocketUrl } from '@/lib/clerk-transport'

export const ReadyState = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const
export type ReadyState = (typeof ReadyState)[keyof typeof ReadyState]

let globalSessionId: string | null = null;
const SESSION_ID_KEY = 'session_id';

export const getSessionId = () => {
  if (!globalSessionId) {
    globalSessionId = localStorage.getItem(SESSION_ID_KEY) || crypto.randomUUID();
    localStorage.setItem(SESSION_ID_KEY, globalSessionId);
  }
  return globalSessionId;
};

export const useWebSocket = (
  url: string | null, 
  options: { 
    reconnectAttempts?: number
    reconnectInterval?: number
    healthCheckInterval?: number
    authToken?: string | null
  } = {}
) => {
  const { 
    reconnectAttempts = 5, 
    reconnectInterval = 3000,
    healthCheckInterval = 30000,  // 30 second health check
    authToken = null
  } = options

  const [readyState, setReadyState] = useState<ReadyState>(ReadyState.CONNECTING)
  const [lastMessage, setLastMessage] = useState<{ data: ServerMessage } | null>(null)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const ws = useRef<WebSocket | null>(null)
  const reconnectCount = useRef(0)
  const reconnectTimeoutRef = useRef<number>()
  const healthCheckTimeoutRef = useRef<number>()
  const mountedRef = useRef(true)
  const sessionId = useMemo(() => getSessionId(), [])
  const messageQueue = useRef<ClientMessage[]>([])

  const connect = useCallback(() => {
    if (!mountedRef.current) return
    if (!url) {
      console.log('No URL provided, skipping connection')
      setReadyState(ReadyState.CLOSED)
      setConnectionError('No WebSocket URL provided')
      return
    }

    if (!authToken) {
      setReadyState(ReadyState.CLOSED)
      setConnectionError('Missing Clerk token')
      return
    }

    const websocketUrl = appendClerkTokenToWebSocketUrl(url, authToken)

    // Validate WebSocket URL
    try {
      const wsUrl = new URL(websocketUrl)
      if (!['ws:', 'wss:'].includes(wsUrl.protocol)) {
        throw new Error('Invalid WebSocket protocol')
      }
    } catch (error) {
      console.error('Invalid WebSocket URL:', error)
      setReadyState(ReadyState.CLOSED)
      setConnectionError(`Invalid WebSocket URL: ${error.message}`)
      return
    }

    // Clear any existing timeouts
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = undefined
    }
    if (healthCheckTimeoutRef.current) {
      window.clearTimeout(healthCheckTimeoutRef.current)
      healthCheckTimeoutRef.current = undefined
    }

    // Close existing connection if any
    if (ws.current) {
      console.log('Closing existing WebSocket connection')
      ws.current.onclose = null // Remove existing onclose handler
      ws.current.onerror = null // Remove existing onerror handler
      ws.current.close()
      ws.current = null
    }

    // Add a small delay before creating new connection
    setTimeout(() => {
      if (!mountedRef.current) return

      try {
        console.log('Attempting WebSocket connection to:', websocketUrl)
        const websocket = new WebSocket(websocketUrl)
        setReadyState(ReadyState.CONNECTING)
        ws.current = websocket

        websocket.onopen = () => {
          if (!mountedRef.current) {
            websocket.close()
            return
          }

          console.log('WebSocket connection established')
          setReadyState(ReadyState.OPEN)
          setConnectionError(null)
          reconnectCount.current = 0

          // Flush any queued messages after a short delay
          if (messageQueue.current.length > 0) {
            console.log('Preparing to flush queued messages:', messageQueue.current.length)
            setTimeout(() => {
              if (!mountedRef.current) return
              if (ws.current !== websocket) return // Ensure this is still the active connection
              if (websocket.readyState === WebSocket.OPEN) {
                const messages = [...messageQueue.current]
                messageQueue.current = []
                
                messages.forEach(queuedData => {
                  try {
                    const msg = JSON.stringify(queuedData)
                    websocket.send(msg)
                    console.log('Sent queued WebSocket message:', { type: queuedData?.type })
                  } catch (error) {
                    console.error('Error sending queued WebSocket message:', error)
                    messageQueue.current.push(queuedData)
                  }
                })
              }
            }, 100)
          }

          // Start health check ping
          const startHealthCheck = () => {
            if (!mountedRef.current) return
            if (ws.current !== websocket) return
            if (websocket.readyState === WebSocket.OPEN) {
              websocket.send(JSON.stringify({ type: 'ping' }))
              healthCheckTimeoutRef.current = window.setTimeout(startHealthCheck, healthCheckInterval)
            }
          }
          startHealthCheck()
        }

        websocket.onclose = (event) => {
          if (!mountedRef.current) return
          if (ws.current !== websocket) return

          const closeInfo = {
            code: event.code,
            reason: event.reason || 'Unknown reason',
            wasClean: event.wasClean
          }
          console.log('WebSocket connection closed:', closeInfo)
          setReadyState(ReadyState.CLOSED)
          setConnectionError(`Connection closed: ${closeInfo.reason} (Code: ${closeInfo.code})`)
          ws.current = null

          // Try to reconnect if we haven't exceeded the limit
          if (mountedRef.current && reconnectCount.current < reconnectAttempts) {
            reconnectCount.current++
            console.log(`Scheduling reconnection attempt ${reconnectCount.current}/${reconnectAttempts} in ${reconnectInterval}ms`)
            reconnectTimeoutRef.current = window.setTimeout(connect, reconnectInterval)
          } else if (reconnectCount.current >= reconnectAttempts) {
            console.error('Max reconnection attempts reached, giving up')
            setConnectionError('Failed to establish connection after maximum retry attempts')
          }
        }

        websocket.onerror = (error) => {
          if (!mountedRef.current) return
          if (ws.current !== websocket) return

          console.error('WebSocket error:', error)
          setConnectionError('Connection error occurred')
          console.error('WebSocket readyState:', websocket.readyState, {
            CONNECTING: websocket.CONNECTING,
            OPEN: websocket.OPEN,
            CLOSING: websocket.CLOSING,
            CLOSED: websocket.CLOSED
          })
        }

        websocket.onmessage = (event) => {
          if (!mountedRef.current) return
          if (ws.current !== websocket) return

          try {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
            if (data.type === 'pong') {
              console.log('Received pong from server')
              return
            }
            console.log('WebSocket message received:', {
              type: data.type,
              messageSize: event.data.length
            })
            setLastMessage({ data })
          } catch (error) {
            console.error('Error parsing WebSocket message:', error)
          }
        }
      } catch (error) {
        console.error('Error creating WebSocket connection:', error)
        setReadyState(ReadyState.CLOSED)
        setConnectionError('Failed to establish connection')
      }
    }, 50) // Small delay before creating new connection
  }, [
    url,
    reconnectAttempts,
    reconnectInterval,
    healthCheckInterval,
    authToken
  ])

  const disconnect = useCallback(() => {
    console.log('Disconnecting WebSocket...')
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = undefined
    }

    if (healthCheckTimeoutRef.current) {
      window.clearTimeout(healthCheckTimeoutRef.current)
      healthCheckTimeoutRef.current = undefined
    }

    if (ws.current) {
      ws.current.close()
    }
  }, [])

  const sendMessage = useCallback((data: ClientMessage) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not open, queuing message:', {
        type: data.type,
        queueLength: messageQueue.current.length + 1
      })
      messageQueue.current.push(data)
      return
    }

    try {
      const msg = JSON.stringify(data)
      ws.current.send(msg)
      console.log('Sent WebSocket message:', { type: data.type })
    } catch (error) {
      console.error('Error sending WebSocket message:', error)
      // Queue the message if send fails
      messageQueue.current.push(data)
    }
  }, [])

  // Re-establish the connection whenever url changes.
  useEffect(() => {
    mountedRef.current = true
    connect()
    return () => {
      mountedRef.current = false
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (healthCheckTimeoutRef.current) {
        clearTimeout(healthCheckTimeoutRef.current)
      }
      if (ws.current) {
        ws.current.close()
      }
    }
  }, [connect, url])

  // Cleanup function
  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current)
      }
      if (healthCheckTimeoutRef.current) {
        window.clearTimeout(healthCheckTimeoutRef.current)
      }
      if (ws.current) {
        ws.current.onclose = null
        ws.current.onerror = null
        ws.current.close()
        ws.current = null
      }
    }
  }, [])

  return { sendMessage, lastMessage, readyState, sessionId, connectionError }
}
